import * as styles from "./ClosureWeeksImpactDialog.css";

import type { JSX } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@frontend/components/atoms/AlertDialog";
import { Plural, Trans, useLingui } from "@lingui/react/macro";

import type { AmendmentImpact } from "../types";

export type ClosureWeeksImpactDialogProps = {
  open: boolean;
  amendments: AmendmentImpact[];
  remaining: number;
  onCancel: () => void;
  onConfirm: () => void;
};

export function ClosureWeeksImpactDialog({
  open,
  amendments,
  remaining,
  onCancel,
  onConfirm,
}: ClosureWeeksImpactDialogProps): JSX.Element {
  const { t } = useLingui();

  const flagged = 0 < amendments.length;
  const shortfall = 0 < remaining;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <Trans>Save these weeks?</Trans>
          </AlertDialogTitle>

          <AlertDialogDescription>
            {flagged ? (
              <Plural
                value={amendments.length}
                one="An amendment was generated with other weeks. Saving flags it for review, and it will have to be generated again."
                other="# amendments were generated with other weeks. Saving flags them for review, and they will have to be generated again."
              />
            ) : (
              <Plural
                value={remaining}
                one="# week is still to place."
                other="# weeks are still to place."
              />
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogBody>
          {flagged ? (
            <ul className={styles.names}>
              {amendments.map((amendment) => (
                <li key={amendment.amendmentId}>
                  {amendment.employeeName ?? t`Unnamed employee`}
                </li>
              ))}
            </ul>
          ) : null}

          {flagged && shortfall ? (
            <p className={styles.shortfall}>
              <Plural
                value={remaining}
                one="# week is still to place."
                other="# weeks are still to place."
              />
            </p>
          ) : null}
        </AlertDialogBody>

        <AlertDialogFooter>
          <AlertDialogCancel>
            <Trans>Keep editing</Trans>
          </AlertDialogCancel>

          <AlertDialogAction onClick={onConfirm}>
            <Trans>Save anyway</Trans>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
